import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Lock, Eye, EyeOff, Key } from 'lucide-react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { useAuthStore } from '../store/useAuthStore';
import ResetPasswordModal from '../components/ResetPasswordModal';
import { getPetImage } from '../lib/petUtils';
import { usePetStore } from '../store/usePetStore';
import CosImage from '../components/CosImage';

export default function Login() {
  const { t } = useTranslation();
  const [isLogin, setIsLogin] = useState(true);
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [activationCode, setActivationCode] = useState('');
  const [isRenew, setIsRenew] = useState(false);
  const [renewActivationCode, setRenewActivationCode] = useState('');
  // 默认开启，rememberedUsername 存储布尔值
  const [rememberMe, setRememberMe] = useState(() => localStorage.getItem('rememberedUsername') !== 'false');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const setAuth = useAuthStore((state) => state.setAuth);
  const navigate = useNavigate();
  const { petConfig, fetchPetConfig } = usePetStore();
  const heroPet = getPetImage('unicorn', 1, petConfig);
  type ApiError = { 
    response?: { 
      data?: { 
        message?: string; 
        code?: string 
      } 
    };
    code?: string;
    message?: string;
  };

  React.useEffect(() => {
    fetchPetConfig();
    // 优先读取 cachedUsername，如果没有则尝试从 rememberedUsername 迁移（兼容旧逻辑）
    const cached = localStorage.getItem('cachedUsername');
    if (cached) {
      setUsername(cached);
    } else {
      const legacy = localStorage.getItem('rememberedUsername');
      // 如果 legacy 存在且不是布尔值字符串，说明是旧版用户名
      if (legacy && legacy !== 'true' && legacy !== 'false') {
        setUsername(legacy);
      }
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const endpoint = isLogin ? (isRenew ? '/auth/renew' : '/auth/login') : '/auth/register';
      const payload = isLogin
        ? isRenew
          ? { username, password, activationCode: renewActivationCode }
          : { username, password }
        : { username: username.trim(), password, activationCode };
      const response = await api.post(endpoint, payload);
      
      const { access_token } = response.data;
      // For now, we decode token or just mock user since backend returns token
      // ideally backend returns user info too, but for MVP we can extract from token or just use username
      const finalUsername = isLogin ? username : username.trim();
      setAuth({ id: 0, username: finalUsername }, access_token);
      
      if (isLogin) {
        // 记录用户是否勾选了“记住用户名”的偏好
        localStorage.setItem('rememberedUsername', String(rememberMe));
        
        if (rememberMe) {
          localStorage.setItem('cachedUsername', username);
        } else {
          localStorage.removeItem('cachedUsername');
        }
      }

      navigate('/');
    } catch (err: unknown) {
      console.error('Login failed:', err);
      const error = err as ApiError;
      const apiMessage = error.response?.data?.message;
      const apiCode = error.response?.data?.code;

      if (isLogin && (apiCode === 'errors.auth.account_expired' || apiMessage === '账号已过期')) {
        setIsRenew(true);
        setError(t('login.errors.expired'));
      } else if (error.code === 'ERR_NETWORK') {
        setError('网络连接失败，请检查网络设置或联系管理员');
      } else {
        // 如果有后端返回的明确错误信息，显示该信息；否则显示系统错误信息以便排查
        setError(apiMessage || (error.message ? `登录请求异常: ${error.message}` : t('login.errors.failed')));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-[#f7fbff] flex items-center justify-center p-4">
      <div
        className="pointer-events-none absolute inset-0 opacity-80"
        style={{
          backgroundImage:
            'radial-gradient(24px 24px at 12% 18%, rgba(255, 184, 213, 0.55) 0%, rgba(255, 184, 213, 0.0) 70%), radial-gradient(26px 26px at 82% 26%, rgba(255, 215, 127, 0.55) 0%, rgba(255, 215, 127, 0.0) 70%), radial-gradient(28px 28px at 70% 80%, rgba(176, 232, 255, 0.60) 0%, rgba(176, 232, 255, 0.0) 70%), radial-gradient(20px 20px at 30% 78%, rgba(186, 255, 216, 0.55) 0%, rgba(186, 255, 216, 0.0) 70%), linear-gradient(135deg, rgba(219, 234, 254, 1) 0%, rgba(255, 241, 250, 1) 45%, rgba(236, 253, 245, 1) 100%)',
        }}
      />
      <div className="pointer-events-none absolute -top-28 -left-28 h-72 w-72 rounded-full bg-[#ffb5e8]/45 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-28 -right-28 h-80 w-80 rounded-full bg-[#b2f7ef]/50 blur-3xl" />
      <div className="pointer-events-none absolute top-12 right-10 h-44 w-44 rounded-full bg-[#ffd166]/45 blur-3xl" />

      <div className="relative w-full max-w-md">
        <div className="text-center mb-7">
          <div className="mx-auto mb-4 h-20 w-20 rounded-[1.75rem] bg-white/70 backdrop-blur-xl border border-white/70 shadow-xl shadow-sky-200/60 flex items-center justify-center">
            <CosImage
              src={heroPet}
              alt="Pet"
              className="h-14 w-14 object-contain drop-shadow-xl dragon-animate"
              draggable={false}
            />
          </div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-800 mb-2">{t('login.appName')}</h1>
          <p className="text-slate-600 text-sm">
            {t('login.tagline1')}
          </p>
          <p className="text-slate-500 text-xs mt-1">
            {t('login.tagline2')}
          </p>
        </div>

        <div className="bg-white/80 backdrop-blur-xl rounded-[1.75rem] shadow-2xl shadow-sky-200/60 border border-white/70 overflow-hidden">
          <div className="p-4 pb-2">
            <div className="grid grid-cols-2 gap-2 bg-white/70 border border-white/70 rounded-2xl p-1">
              <button
                type="button"
                className={clsx(
                  'rounded-2xl py-3 text-sm font-extrabold transition-all',
                  isLogin
                    ? 'bg-white text-[#ff4da6] shadow-md shadow-sky-200/70'
                    : 'text-slate-600 hover:text-slate-800'
                )}
                onClick={() => {
                  setIsLogin(true);
                  setIsRenew(false);
                  setRenewActivationCode('');
                }}
              >
                {t('login.tabs.login')}
              </button>
              <button
                type="button"
                className={clsx(
                  'rounded-2xl py-3 text-sm font-extrabold transition-all',
                  !isLogin
                    ? 'bg-white text-[#ff4da6] shadow-md shadow-sky-200/70'
                    : 'text-slate-600 hover:text-slate-800'
                )}
                onClick={() => {
                  setIsLogin(false);
                  setIsRenew(false);
                  setRenewActivationCode('');
                }}
              >
                {t('login.tabs.register')}
              </button>
            </div>
          </div>

          <div className="px-8 pt-6 pb-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="bg-rose-50 text-rose-600 text-sm p-3 rounded-2xl border border-rose-100">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700">{t('login.username.label')}</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-slate-400" />
                  </div>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => {
                      setUsername(e.target.value);
                      if (isRenew) setIsRenew(false);
                    }}
                    className="block w-full pl-11 pr-4 py-3 border border-slate-200 rounded-2xl bg-white/90 focus:ring-4 focus:ring-sky-100 focus:border-sky-300 outline-none transition-all text-sm"
                    placeholder={t('login.username.placeholder')}
                    required
                    autoComplete="username"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700">{t('login.password.label')}</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-slate-400" />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (isRenew) setIsRenew(false);
                    }}
                    className="block w-full pl-11 pr-12 py-3 border border-slate-200 rounded-2xl bg-white/90 focus:ring-4 focus:ring-sky-100 focus:border-sky-300 outline-none transition-all text-sm"
                    placeholder={t('login.password.placeholder')}
                    required
                    autoComplete={isLogin ? 'current-password' : 'new-password'}
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="h-9 w-9 rounded-xl flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-colors"
                      aria-label={showPassword ? t('login.password.hide') : t('login.password.show')}
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
              </div>

              {!isLogin && (
                <div className="space-y-2 animate-in fade-in slide-in-from-top-2 duration-300">
                  <label className="text-sm font-bold text-slate-700">{t('login.activationCode.label')}</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Key className="h-5 w-5 text-slate-400" />
                    </div>
                    <input
                      type="text"
                      value={activationCode}
                      onChange={(e) => setActivationCode(e.target.value)}
                      className="block w-full pl-11 pr-4 py-3 border border-slate-200 rounded-2xl bg-white/90 focus:ring-4 focus:ring-amber-100 focus:border-amber-300 outline-none transition-all text-sm"
                      placeholder={t('login.activationCode.placeholder')}
                      required
                    />
                  </div>
                  <p className="text-xs text-slate-500">
                    {t('login.activationCode.hint')}
                  </p>
                </div>
              )}

              {isLogin && isRenew && (
                <div className="space-y-2 animate-in fade-in slide-in-from-top-2 duration-300">
                  <label className="text-sm font-bold text-slate-700">{t('login.renewCode.label')}</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Key className="h-5 w-5 text-slate-400" />
                    </div>
                    <input
                      type="text"
                      value={renewActivationCode}
                      onChange={(e) => setRenewActivationCode(e.target.value)}
                      className="block w-full pl-11 pr-4 py-3 border border-slate-200 rounded-2xl bg-white/90 focus:ring-4 focus:ring-amber-100 focus:border-amber-300 outline-none transition-all text-sm"
                      placeholder={t('login.renewCode.placeholder')}
                      required
                    />
                  </div>
                  <p className="text-xs text-slate-500">
                    {t('login.renewCode.hint')}
                  </p>
                </div>
              )}

              {isLogin && (
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="h-4 w-4 rounded border-slate-300 text-[#ff4da6] focus:ring-[#ff4da6] accent-[#ff4da6]"
                  />
                  <label htmlFor="remember-me" className="ml-2 block text-sm text-slate-600 select-none cursor-pointer">
                    {t('login.rememberMe')}
                  </label>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-3 px-4  border-transparent rounded-2xl shadow-xl shadow-pink-200/60 text-sm font-extrabold text-white bg-gradient-to-r from-[#ff4da6] via-[#ff7a59] to-[#ffd166] hover:brightness-105 focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-pink-200 disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.99] transition-all"
              >
                {loading ? t('login.processing') : (isLogin ? (isRenew ? t('login.submit.renewAndLogin') : t('login.submit.login')) : t('login.submit.register'))}
              </button>
            </form>

            {isLogin && (
              <div className="mt-6 text-center">
                <button
                  type="button"
                  onClick={() => setIsResetModalOpen(true)}
                  className="text-sm text-slate-600 hover:text-slate-800 font-semibold"
                >
                  {t('login.forgotPassword')}
                </button>
              </div>
            )}

            <div className="mt-6 text-center">
              <span className="text-sm text-slate-600">
                {isLogin ? t('login.switch.noAccount') : t('login.switch.hasAccount')}
              </span>
              <button
                type="button"
                className="ml-1 text-sm font-extrabold text-[#ff4da6] hover:text-[#ff2f97]"
                onClick={() => setIsLogin(!isLogin)}
              >
                {isLogin ? t('login.switch.toRegister') : t('login.switch.toLogin')}
              </button>
            </div>
          </div>
        </div>
      </div>

      <ResetPasswordModal isOpen={isResetModalOpen} onClose={() => setIsResetModalOpen(false)} />
    </div>
  );
}
